import { FunctionComponent, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Daycare } from '@/types/Daycare'
import { StaffPreparationTask } from '@/types/StaffPreparationTask'
import { GET_STAFF_PREPARATION_TASKS_BY_DAYCARE } from '@/modules/API/staff/staffPreparationTask'
import { useQuery } from '@apollo/client'
import styles from './staffPreparationTask.module.scss'
import Title from '@/components/_custom/Title/title'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import Loading from '@/components/Loading/Loading'
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material'
import StaffPreparationTaskRow from './staffPreparationTaskTableRow'
import StaffPreparationTaskDialog from './Dialog/staffPreparationTaskDialog'
import ErrorMessage from '@/components/_custom/ErrorMessage/ErrorMessage'
import { useDaycareRight } from '@/hooks/useDaycareRight'

interface StaffPreparationTaskProps {
    daycare: Daycare
    className?: string
}

const StaffPreparationTaskComponent: FunctionComponent<StaffPreparationTaskProps> = ({
    daycare,
    className
}: StaffPreparationTaskProps) => {
    const canUpdate: boolean = useDaycareRight('canWriteParamsDaycare')
    const { t } = useTranslation()

    const [staffPreparationTaskList, setStaffPreparationTaskList] = useState<Array<StaffPreparationTask>>([])

    const [isStaffPreparationTaskDialogOpen, setStaffPreparationTaskDialogOpen] = useState<boolean>(false)

    const {
        loading: loadingStaffPreparationTasks,
        error: errorStaffPreparationTasks,
        data: dataStaffPreparationTasks
    } = useQuery<{
        getStaffPreparationTasksByDaycare: Array<StaffPreparationTask>
    }>(GET_STAFF_PREPARATION_TASKS_BY_DAYCARE, {
        fetchPolicy: 'network-only',
        variables: {
            daycareId: daycare.id
        }
    })

    function addOneStaffPreparationTask(staffPreparationTask: StaffPreparationTask): void {
        setStaffPreparationTaskList([...staffPreparationTaskList, staffPreparationTask])
    }

    function removeOneStaffPreparationTask(staffPreparationTaskId: number): void {
        setStaffPreparationTaskList(
            staffPreparationTaskList.filter(
                (existingStaffPreparationTask: StaffPreparationTask) =>
                    existingStaffPreparationTask.id !== staffPreparationTaskId
            )
        )
    }

    function updateOneStaffPreparationTask(staffPreparationTask: StaffPreparationTask): void {
        setStaffPreparationTaskList(
            staffPreparationTaskList.map((existingStaffPreparationTask: StaffPreparationTask) =>
                existingStaffPreparationTask.id === staffPreparationTask.id
                    ? staffPreparationTask
                    : existingStaffPreparationTask
            )
        )
    }

    useEffect(() => {
        if (dataStaffPreparationTasks) {
            setStaffPreparationTaskList(dataStaffPreparationTasks.getStaffPreparationTasksByDaycare)
        }
    }, [dataStaffPreparationTasks])

    return (
        <div className={`${styles.StaffPreparationTaskPlacement} ${className || ''}`}>
            <div className={styles.Form}>
                <div className={styles.TitleAndButton}>
                    <Title text={t('myStaffPreparationTasks')} className={styles.Title} />
                    {canUpdate && (
                        <ButtonTextIcon
                            type='add'
                            className={styles.Button}
                            onClick={() => setStaffPreparationTaskDialogOpen(true)}
                        />
                    )}
                </div>
                {loadingStaffPreparationTasks && <Loading overall={false} />}
                {dataStaffPreparationTasks && daycare.staffAccess && (
                    <>
                        <TableContainer className={styles.TablePlacement}>
                            <Table className={styles.Table}>
                                <TableHead className={styles.TableHeader}>
                                    <TableRow>
                                        <TableCell>{t('task')}</TableCell>
                                        <TableCell>{t('duration')}</TableCell>
                                        {canUpdate && (
                                            <TableCell colSpan={2} sx={{ width: '10%' }}>
                                                {t('actions')}
                                            </TableCell>
                                        )}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {staffPreparationTaskList.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={4}>{t('noStaffPreparationTask')}</TableCell>
                                        </TableRow>
                                    ) : (
                                        staffPreparationTaskList.map(
                                            (staffPreparationTask: StaffPreparationTask, index: number) => (
                                                <StaffPreparationTaskRow
                                                    key={
                                                        staffPreparationTask.id +
                                                        staffPreparationTask.name +
                                                        staffPreparationTask.duration +
                                                        index
                                                    }
                                                    staffPreparationTask={staffPreparationTask}
                                                    daycare={daycare}
                                                    canUpdate={canUpdate}
                                                    removeOneStaffPreparationTask={removeOneStaffPreparationTask}
                                                    updateOneStaffPreparationTask={updateOneStaffPreparationTask}
                                                />
                                            )
                                        )
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <StaffPreparationTaskDialog
                            open={isStaffPreparationTaskDialogOpen}
                            handleClose={() => setStaffPreparationTaskDialogOpen(false)}
                            mode='create'
                            addOneStaffPreparationTask={addOneStaffPreparationTask}
                            daycare={daycare}
                        />
                    </>
                )}
                {errorStaffPreparationTasks && <ErrorMessage error={t('generalError')} />}
            </div>
        </div>
    )
}

export default StaffPreparationTaskComponent
